import React, { JSX, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { Label, LabelProps, Pie, PieChart, PieLabelRenderProps, PieSectorShapeProps, ResponsiveContainer, Sector, Tooltip, TooltipContentProps } from 'recharts';
import { NameType, ValueType } from 'recharts/types/component/DefaultTooltipContent';
import { RADIAN } from "@app/overviews/market-expansion-dashboard/MarketExpansionDashboardConstants";
import MarketShareSkeleton from "@app/overviews/market-expansion-dashboard/components/MarketShareSkeleton";
import { MarketExpansionFiltersContext } from "@app/overviews/market-expansion-dashboard/contexts/MarketExpansionFiltersContext";
import { useMarketShareChartData } from "@app/overviews/market-expansion-dashboard/hooks/useMarketShareChartData";
import { MarketShareChartData } from "@app/overviews/market-expansion-dashboard/shared/MarketShareChartData";
import { MARKET_EXPANSION_LINE_OF_BUSINESS_CONFIG } from "@app/overviews/market-expansion-dashboard/shared/marketExpansionLineOfBusinessConfig";
import { QesIcon } from "@app/shared/icons/QESIcon";
import { QesIcons } from "@app/shared/icons/qesIcons";

interface MarketSharePieChartProps {
  animationId: number;
  data: MarketShareChartData[];
  metricLabel?: string;
  total: number;
  totalLabelText?: string;
}

const MarketSharePieChart = React.memo(({ animationId, data, metricLabel, total, totalLabelText }: MarketSharePieChartProps): JSX.Element => {
  const renderPayerMetricPercentageLabel = useCallback(({ cx, cy, midAngle, innerRadius, outerRadius, percent, index }: PieLabelRenderProps) => {
    if (cx == null || cy == null || innerRadius == null || outerRadius == null) {
      return null;
    }
    const radius = Number(innerRadius) + (Number(outerRadius) - Number(innerRadius)) * 0.5;
    const ncx = Number(cx);
    const x = ncx + radius * Math.cos(-(midAngle ?? 0) * RADIAN);
    const ncy = Number(cy);
    const y = ncy + radius * Math.sin(-(midAngle ?? 0) * RADIAN);
    const textColor = data[Number(index)]?.textColor;
    const percentValue = ((percent ?? 1) * 100).toFixed(0);
    const label = percentValue === '0' ? '<1%' : `${percentValue}%`;

    return (
      <text x={x} y={y} className="market-share-tile__percent-label" dominantBaseline="central" fill={textColor}>
        {label}
      </text>
    );
  }, [data]);

  const renderTotalLabel = useCallback(({ viewBox }: LabelProps) => {
    const { cx, cy } = (viewBox ?? {}) as { cx?: number; cy?: number };
    return (
      <g className="market-share-tile__total-metric-label">
        <text x={cx} y={cy} dy={-8} textAnchor="middle">
          {totalLabelText}
        </text>
        <text x={cx} y={cy} dy={12} textAnchor="middle">
          {total.toLocaleString()}
        </text>
      </g>
    )
  }, [total, totalLabelText]);

  const renderTooltipContent = useCallback(({ active, payload }: TooltipContentProps<ValueType, NameType>) => {
    if (!active || !payload?.length) return null;
    return (
      <div className="market-share-tile__tooltip">
        <strong>{payload[0].name}</strong><br />
        <text>{metricLabel}: {(payload[0].value as number).toLocaleString()}</text>
      </div>
    )
  }, [metricLabel]);

  const renderSector = useCallback((shapeProps: PieSectorShapeProps) => (
    <Sector {...shapeProps} fill={shapeProps.payload.fillColor} />
  ), []);

  return (
    <ResponsiveContainer width="100%" height="100%" className="market-share-tile__container">
      <PieChart>
        <Pie
          key={animationId}
          data={data}
          label={renderPayerMetricPercentageLabel}
          labelLine={false}
          innerRadius="70%"
          outerRadius="100%"
          cornerRadius="10%"
          paddingAngle={2}
          minAngle={10}
          dataKey="count"
          cx="50%"
          cy="50%"
          shape={renderSector}
        >
          <Label content={renderTotalLabel} />
        </Pie>
        <Tooltip content={renderTooltipContent} position={{ x: 0 }} />
      </PieChart>
    </ResponsiveContainer>
  );
});
MarketSharePieChart.displayName = 'MarketSharePieChart';

const MarketShareTile = (): JSX.Element => {
  const filtersContext = useContext(MarketExpansionFiltersContext);

  const [marketShareTitle, setMarketShareTitle] = useState<string>("");
  const [animationId, setAnimationId] = useState(0);
  const [previousChartSignature, setPreviousChartSignature] = useState<string | null>(null);
  const [otherExpanded, setOtherExpanded] = useState(false);
  const [marketShareDataAvailable, setMarketShareDataAvailable] = useState(false);

  const lineOfBusiness = filtersContext.selectedLineOfBusiness;
  const lineOfBusinessConfig = lineOfBusiness ? MARKET_EXPANSION_LINE_OF_BUSINESS_CONFIG[lineOfBusiness] : undefined;

  const marketShareChartData = useMarketShareChartData();

  const renderLegend = () => (
    <ul className="market-share-tile__legend-list">
      {marketShareChartData.data.map((entry, index) => {
        const isOther = entry.name === 'Other';
        const hasIncludedPayers = isOther && (entry.includedPayers?.length ?? 0) > 0;
        return (
          <li key={index} className="market-share-tile__legend-item">
            <div className="market-share-tile__legend-item-header">
              <span className="market-share-tile__legend-color" style={{ backgroundColor: entry.fillColor }} />
              <span className="market-share-tile__legend-name">{entry.name}</span>
              {hasIncludedPayers && (
                <button
                  className="market-share-tile__legend-expand-btn"
                  onClick={() => setOtherExpanded(x => !x)}
                  data-testid="market-share-tile__toggle-other-list-btn"
                >
                  {otherExpanded ?
                    <QesIcon iconName={QesIcons.ChevronUp} description="Collapse" />
                    :
                    <QesIcon iconName={QesIcons.ChevronDown} description="Expand" />
                  }
                </button>
              )}
            </div>
            {hasIncludedPayers && otherExpanded && (
              <ul className="market-share-tile__legend-other-list">
                {(entry.includedPayers ?? []).map((payer, i) => (
                  <li key={i} className="market-share-tile__legend-other-item">{payer}</li>
                ))}
              </ul>
            )}
          </li>
        );
      })}
    </ul>
  );

  // Identifies the chart by content instead of the recalculation reference, so a
  // no-op update doesn't retrigger the data change animation.
  const chartSignature = useMemo(
    () => JSON.stringify(marketShareChartData.data.map(entry => [entry.name, entry.count, entry.includedPayers])),
    [marketShareChartData.data]
  );

  // Remounting <Pie> (via its key) replays the slice transition when the chart content changes
  if (chartSignature !== previousChartSignature) {
    setPreviousChartSignature(chartSignature);
    setOtherExpanded(false);
    if (marketShareChartData.hasData) {
      setAnimationId(id => id + 1);
    }
  }

  const hasCountiesSelected = !!filtersContext.selectedState && filtersContext.selectedCounties.length > 0;
  // Distinguishes "still fetching/no selection" from "fetch completed but returned zero payers for this Service Area"
  const fetchCompletedWithNoData = hasCountiesSelected && !marketShareChartData.isLoading && !marketShareChartData.isError && !marketShareChartData.hasData;

  useEffect(() => {
    if (!filtersContext.selectedLineOfBusiness || !hasCountiesSelected || fetchCompletedWithNoData) {
      setMarketShareDataAvailable(false);
      setMarketShareTitle("No data available");
    } else if (filtersContext.allCountiesSelected) {
      setMarketShareDataAvailable(true);
      setMarketShareTitle(filtersContext.selectedState!);
    } else {
      setMarketShareDataAvailable(true);
      setMarketShareTitle("Selected Service Area");
    }
  }, [fetchCompletedWithNoData, filtersContext.allCountiesSelected, filtersContext.selectedLineOfBusiness, filtersContext.selectedState, hasCountiesSelected]);

  return (
    <React.Fragment>
      <div className="market-expansion-dashboard__market-share-tile">
        <h2 className="qes-dashboard-tile__title ps-5">Market Share <span style={{ fontStyle: marketShareDataAvailable ? "normal" : "italic" }}>{` - ${marketShareTitle}`}</span></h2>
        {marketShareChartData.isError ?
          // TODO: Decision on tile specific error messages is currently being made. User Story #208126 will add standardized error view if required.
          <div className="market-share-tile__error" role="alert">Unable to load Market Share data.</div>
          : fetchCompletedWithNoData ?
          <div className="market-share-tile__empty" data-testid="market-share-tile__empty">No data available</div>
          : marketShareChartData.isLoading || !marketShareChartData.hasData ?
          <MarketShareSkeleton />
          :
          <div className="market-share-tile__split-container">
            <div className="market-share-tile__chart-half">
              <MarketSharePieChart
                animationId={animationId}
                data={marketShareChartData.data}
                metricLabel={lineOfBusinessConfig?.metricLabel}
                total={marketShareChartData.total}
                totalLabelText={lineOfBusinessConfig?.marketShareTotalLabel}
              />
            </div>
            <div className="market-share-tile__legend-half">
              {renderLegend()}
            </div>
          </div>
        }
      </div>
    </React.Fragment>
  );
};

export default MarketShareTile;
